import { Analytics } from '@util/Analytics';
import { Environment } from '@util/Environment';
import 'firebase/compat/remote-config';
import { App } from 'hydra-data-types';
import { SimpleEventEmitter } from 'ts-utils';

export type VariantsConfig = Record<string, string>;
export type VariantOf<VC extends VariantsConfig> = VC[keyof VC];

/** When should an experiment be recorded as having been viewed by the user? */
export enum MarkViewedOn {
  /** Record viewed on app startup */
  Init,
  /** Record viewed when `useExperiment`, `useExperimentSwitch` or `ExperimentSwitch` is first used. */
  React,
  /** Record viewed only on an explicit call to `viewed()`. */
  None,
}

export type ExperimentConfig<Variants extends VariantsConfig> = {
  /** The experiment name e.g. `MoneyMaker`. */
  name: string;
  variants: Variants;
  default: VariantOf<Variants>;
  markViewedOn: MarkViewedOn;
  /**
   * Activate the experiment only on certain apps. Default is all apps.
   *
   * For inactive apps, `variant` will return the `defaultVariant`.
   */
  activeApps?: App[];
  /**
   * Exclude all variants of this experiment from automatically being rotated through in integration tests
   *
   * Useful for config based experiments that don't affect other functionality
   */
  excludeFromIntegrationTests?: boolean;
};

/** Base abstract experiment to be extended by a concrete implementation (e.g. `RemoteConfigExperiment`). */
export abstract class Experiment<Variants extends VariantsConfig> {
  /** The experiment name e.g. `MoneyMaker`. */
  public readonly name: string;
  /** Variants mapping. For an array, use `experiment.allVariants` instead. */
  public readonly variants: Variants;
  /** The **default variant** which will be active until values are initialized. */
  public readonly defaultVariant: VariantOf<Variants>;
  public readonly activeApps?: App[];
  public readonly isActive: boolean;
  public readonly onChange = new SimpleEventEmitter();
  public readonly markViewedOn: MarkViewedOn;
  public readonly excludeFromIntegrationTests?: boolean;
  private override: VariantOf<Variants> | undefined;
  private variantLastViewed: VariantOf<Variants> | undefined;

  public constructor(config: ExperimentConfig<Variants>) {
    this.name = config.name;
    this.variants = config.variants;
    this.defaultVariant = config.default;
    this.markViewedOn = config.markViewedOn;
    this.activeApps = config.activeApps;
    this.isActive = config.activeApps?.includes(Environment.APP) ?? true;
    this.excludeFromIntegrationTests = config.excludeFromIntegrationTests;

    // Bind methods so they can be deconstructed from the instance if needed.
    this.variant = this.variant.bind(this);
    this.overrideVariant = this.overrideVariant.bind(this);
    this.reset = this.reset.bind(this);
    this.viewed = this.viewed.bind(this);
    this.succeed = this.succeed.bind(this);
    this.select = this.select.bind(this);

    // Ensure analytics reflects the current variant
    this.onChange.addListener(() => this.setUserExperimentProperty());
  }

  /** Override this to get the current active variant from a source like RemoteConfig or query params.
   * If the variant isn't ready or can't be found, return `undefined` and the default variant will be used. */
  protected abstract getVariantFromSource(): VariantOf<Variants> | undefined;

  /** The current **active variant**. */
  public variant(): VariantOf<Variants> {
    return this.isActive
      ? (this.override ?? this.getVariantFromSource() ?? this.defaultVariant)
      : this.defaultVariant;
  }

  /**
   * Selects a value based on the current active variant.
   *
   * ```tsx
   * const exampleGreeting = Experiments.NervaGreetings.select({ OptionA: 'Hi', OptionB: 'Hello' });
   * ```
   */
  public select<Value, Values extends Record<VariantOf<Variants>, Value>>(
    values: Values
  ): Values[keyof Values] {
    return values[this.variant()];
  }

  /** Variants as an array. For a mapping, use `experiment.variants` instead. */
  public get allVariants(): VariantOf<Variants>[] {
    return Object.values(this.variants) as unknown as VariantOf<Variants>[]; // ts gets confused otherwise
  }

  /** Override the current variant. Note: the `configValue` will remain unchanged. */
  public overrideVariant(variant: VariantOf<Variants>): void {
    this.override = variant;
    this.onChange.dispatch();
  }

  public get isOverridden(): boolean {
    return this.override !== undefined;
  }

  /** Reset the experiment to its initial non-overridden state. */
  public reset(): void {
    this.override = undefined;
    this.variantLastViewed = undefined;
    this.onChange.dispatch();
  }

  /**
   * Record the current experiment variant being viewed by emitting an `o-experiment-viewed` event.
   *
   * It will automatically deduplicate (drop) subsequent calls made for the same variant, unless `force=true`.
   */
  public viewed(options?: { force: boolean }): void {
    if (this.isActive) {
      const currentVariant = this.variant();
      const variantHasChanged = this.variantLastViewed !== currentVariant;
      const forceViewed = options?.force ?? false;

      if (variantHasChanged || forceViewed) {
        this.track(Analytics.Event.o_experiment_viewed);
        this.variantLastViewed = currentVariant;
      }
    }
  }

  /** Record the current experiment variant succeeding by emitting an `o-experiment-converted` event. */
  public succeed(): void {
    if (this.isActive) {
      this.track(Analytics.Event.o_experiment_converted);
    }
  }

  private track(
    event:
      | typeof Analytics.Event.o_experiment_viewed
      | typeof Analytics.Event.o_experiment_converted
  ) {
    Analytics.track({
      event,
      experimentName: this.name,
      variationName: this.variant(),
    });
  }

  private setUserExperimentProperty(): void {
    Analytics.identify({
      experimentVariants: {
        [this.name]: this.variant(),
      },
    });
  }
}
